import * as SVG from 'svg.js';

import { Utils } from 'core/utils';
import { CoordinateTranslation } from './coordinate-translation';
import { CropHandleCorner, CropHandlePosition } from './crop-handle-corner';
import { DragInfo, DragSensor } from './drag-sensor';
import { pinSVGElement, Region, RegionLike } from './region';
import { RegionScaler } from './region-scaler';

class CropShadow
{
  private background: SVG.Rect;
  private shape: SVG.Shape;

  constructor(private svgDoc: SVG.Doc, private cutOut: SVG.Shape)
  {
    this.background = new SVG.Rect().fill('white');

    const mask =
        svgDoc
          .mask()
          .add(this.background)
          .add(this.cutOut);

    this.shape =
        this
          .svgDoc
          .rect()
          .addClass('image-cropper-shadow')
          .maskWith(mask);

    this.reconfigure();
  }

  public reconfigure(): void
  {
    const bounds = this.svgDoc.rbox();

    this.background.size(bounds.width, bounds.height);
    this.shape.size(bounds.width, bounds.height);
  }
}

interface PercentageWrapper {
  host: SVG.Nested;
  content: SVG.Element;
}

function wrapPercentage(content: SVG.Element): PercentageWrapper
{
  const parent = content.parents()[0] as SVG.Container;

  const wrapper = {
    host: (parent
           .nested()
           .viewbox(0, 0, 100, 100)
           .attr('preserveAspectRatio', 'none')),
    content: content,
  };

  wrapper.host.add(content);

  return wrapper;
}

export class CropHandle
{
  private readonly outline: SVG.Rect;
  private readonly corners: CropHandleCorner[];
  private readonly shadow: CropShadow;

  private readonly outlineBorder: PercentageWrapper;
  private readonly outlineBorderLine: SVG.Rect;
  private readonly cutOutMask: PercentageWrapper;

  private readonly moveSensor: DragSensor;
  private readonly scaleSensors: DragSensor[];

  private constrainRegion: Region = undefined;
  private viewportRegion: Region = undefined;
  private translation: CoordinateTranslation = undefined;

  private ephemeralScaler: RegionScaler;

  constructor(
      private readonly svgDoc: SVG.Doc,
      private readonly cutOutShape: SVG.Shape,
      private readonly selectCallback: (r: Region) => void)
  {
    this.shadow = new CropShadow(svgDoc, cutOutShape);

    this.outline = svgDoc.rect().addClass('image-cropper-outline');

    this.outlineBorder = wrapPercentage(svgDoc
                                        .rect(100, 100)
                                        .fill('rgba(0, 0, 0, 0)')
                                        .style('pointer-events', 'none'));

    this.cutOutMask = wrapPercentage(
      svgDoc.rect(100, 100).fill('white'));

    this.outlineBorderLine = (svgDoc
                              .rect()
                              .addClass('image-cropper-outline-border'));

    this.cutOutShape.maskWith(this.cutOutMask.host);

    this.corners = [
        CropHandlePosition.TopLeft,
        CropHandlePosition.TopRight,
        CropHandlePosition.BottomLeft,
        CropHandlePosition.BottomRight
    ].map((pos) => new CropHandleCorner(this.svgDoc, this.outline, pos));

    this.moveSensor = new DragSensor(this.outline);
    this.moveSensor.onDrag = this.onMoveSensorDrag.bind(this);
    this.moveSensor.onDragEnd = this.onSelected.bind(this);

    this.scaleSensors = this.corners.map((corner) => {
      const sensor = new DragSensor(corner.shape);

      sensor.onDragBegin = (info) => this.onScaleSensorDragBegin(corner);
      sensor.onDrag = this.onScaleSensorDrag.bind(this);
      sensor.onDragEnd = this.onSelected.bind(this);

      return sensor;
    });
  }

  public destructor(): void
  {
    for (const sensor of this.scaleSensors) {
      sensor.destructor();
    }
    this.moveSensor.destructor();
  }

  private onSelected(): void {
    this.selectCallback(
        this.translation.regionToTargetSpace(this.viewportRegion));
  }

  public reconfigure(
      constrainRegion: Region,
      translation: CoordinateTranslation,
      targetRegion: Region,
      editableArea: RegionLike): void
  {
    this.constrainRegion = constrainRegion;
    this.translation = translation;

    this.viewportRegion = this.constrainRegion.clipRegion(
      this.translation.regionToViewportSpace(targetRegion));

    this.shadow.reconfigure();

    if (editableArea) {
      const editableRegion = Region.fromSimilar(editableArea);
      pinSVGElement(this.outlineBorder.content, editableRegion);
      pinSVGElement(this.cutOutMask.content, editableRegion);
    }

    this.scheduleUpdate();
  }

  private onMoveSensorDrag(dragInfo: DragInfo): void
  {
    this.viewportRegion =
        this.constrainRegion.clampRegion(
            new Region(
                dragInfo.elementSnapshot.x + dragInfo.delta.x,
                dragInfo.elementSnapshot.y + dragInfo.delta.y,
                this.viewportRegion.width,
                this.viewportRegion.height));

    this.scheduleUpdate();

    if (!Utils.isSmallDevice()) {
      this.onSelected();
    }
  }

  private onScaleSensorDragBegin(corner: CropHandleCorner): void
  {
    const minSize = corner.sideLength * 2;

    this.ephemeralScaler =
        new RegionScaler(
            this.viewportRegion,
            new Region(0, 0, minSize, minSize),
            this.constrainRegion,
            corner.position);
  }

  private onScaleSensorDrag(dragInfo: DragInfo): void
  {
    const dragPoint = this.ephemeralScaler.samePoint.add(dragInfo.delta);

    this.viewportRegion =
        this.ephemeralScaler.applyScalePoint(this.viewportRegion, dragPoint);

    this.scheduleUpdate();

    if (!Utils.isSmallDevice()) {
      this.onSelected();
    }
  }

  public set targetRegion(target: Region)
  {
    this.viewportRegion = this.translation.regionToViewportSpace(target);
    this.scheduleUpdate();
  }

  public get targetRegion(): Region
  {
    return this.translation === undefined
        ? undefined
        : this.translation.regionToTargetSpace(this.viewportRegion);
  }

  private scheduleUpdate(): void
  {
    window.requestAnimationFrame(this.update.bind(this));
  }

  private update(): void
  {
    pinSVGElement(this.outline, this.viewportRegion);
    pinSVGElement(this.outlineBorder.host, this.viewportRegion);
    pinSVGElement(this.cutOutShape, this.viewportRegion);
    pinSVGElement(this.cutOutMask.host, this.viewportRegion);

    pinSVGElement(this.outlineBorderLine, Region.fromRBox(
      (this.outlineBorder.content as any).rbox(this.svgDoc)));

    for (const corner of this.corners) {
      corner.update();
    }
  }
}



// WEBPACK FOOTER //
// ./src/app/controls/image-cropper/crop-handle.ts